import type { DevDomainConfig, DevDomainId } from '../types/developmentDemo'

const seededAt = '2026-09-02T10:00:00.000Z'
const seededReviewAt = '2026-09-03T14:30:00.000Z'

export const developmentCycle = ['Assess', 'Review', 'Develop', 'Reassess']

export const criterionLevelLabels = {
  'not-yet': 'Not yet evidenced',
  partly: 'Partly evidenced',
  evidenced: 'Evidenced in this task',
} as const

export const illustrativeRubricNote =
  'Demonstration criteria are illustrative task rubrics. They are not validated CJI or NCJI instruments and produce no overall score or rank.'

const education: DevDomainConfig = {
  id: 'education',
  productLabel: 'Noetivra for Education',
  title: 'Education & Learning',
  description: 'Build reasoning and self-direction through evidence, feedback and practice.',
  organization: 'Nordland Adult Learning Studio',
  participantNoun: 'Learner',
  reviewerRole: 'Educator',
  workspaces: {
    participant: {
      title: 'Learner Workspace',
      body: 'Work through an evidence task, read your educator’s feedback, practise and return to a changed scenario.',
      buttonLabel: 'Enter Learner Workspace',
    },
    reviewer: {
      title: 'Educator Workspace',
      body: 'Inspect submissions, give criterion-based feedback, assign practice and compare reassessment evidence.',
      buttonLabel: 'Enter Educator Workspace',
    },
  },
  participants: [
    { id: 'EDU-P01', name: 'Maya Chen', description: 'Adult learner focused on evidence and planning' },
    { id: 'EDU-P02', name: 'Sam Okoro', description: 'Adult learner returning to study' },
  ],
  criteria: [
    { id: 'evidence-use', label: 'Evidence use', description: 'Draws conclusions the reports actually support.' },
    { id: 'limitations', label: 'Recognition of limitations', description: 'Names uncertainties such as self-selection and missing comparisons.' },
    { id: 'next-step', label: 'Next-step planning', description: 'Proposes a proportionate, practical next step.' },
  ],
  tasks: {
    initial: {
      id: 'EDU-T1',
      round: 'initial',
      title: 'What does the course evidence actually support?',
      context:
        'A fictional course reports improved task completion among 60 self-selected volunteers. A second report from three sites finds mixed results and uneven attendance. Neither has a matched comparison group.',
      prompt: 'Explain what the reports support, identify two important uncertainties and propose a proportionate next step.',
    },
    reassessment: {
      id: 'EDU-T2',
      round: 'reassessment',
      title: 'Follow-up: a new site reports no change',
      context:
        'A new site reports no change but had half the attendance of the original site. Revisit your interpretation and propose a fair follow-up comparison.',
      prompt: 'Revisit your interpretation and propose a fair follow-up comparison.',
    },
  },
  suggestedActivity: {
    title: 'Two explanations for one observation',
    instructions: 'Compare one observation with two alternative explanations. Describe what evidence would distinguish them.',
  },
  features: {},
  note: 'Feedback supports learning. It is not a grade, ranking or record of ability.',
  seed: {
    submissions: [
      {
        id: 'SUB-001',
        participantId: 'EDU-P02',
        taskVersionId: 'EDU-T1',
        round: 'initial',
        response:
          'The first report shows the course worked because 60 people improved. The second report is mixed, maybe because attendance was uneven. One uncertainty is that people chose to join. I would run the course at more sites.',
        at: seededAt,
      },
    ],
    reviews: [],
    activities: [],
  },
}

const talent: DevDomainConfig = {
  id: 'talent',
  productLabel: 'Noetivra Talent',
  title: 'Employers & Workforce',
  description: 'Connect role requirements to work samples, transparent review and development.',
  organization: 'Meridian Projects — Project Coordinator',
  participantNoun: 'Candidate',
  reviewerRole: 'Employer reviewer',
  workspaces: {
    participant: {
      title: 'Candidate / Employee Workspace',
      body: 'Complete a role-based work sample, see how it was reviewed, follow development activities and the recorded stage decision.',
      buttonLabel: 'Enter Candidate Workspace',
    },
    reviewer: {
      title: 'Employer Reviewer Workspace',
      body: 'Review work samples against the same role criteria, compare evidence side by side and record human decisions with reasons.',
      buttonLabel: 'Enter Reviewer Workspace',
    },
  },
  participants: [
    { id: 'TAL-P01', name: 'Nadia Ellis', description: 'Candidate · Project Coordinator' },
    { id: 'TAL-P02', name: 'Owen Park', description: 'Candidate · Project Coordinator' },
  ],
  criteria: [
    { id: 'dependencies', label: 'Dependency planning', description: 'Identifies dependencies and sequences work around them.' },
    { id: 'trade-offs', label: 'Resource trade-offs', description: 'Explains a budget or staffing trade-off and its consequences.' },
    { id: 'communication', label: 'Communication', description: 'Writes a concise, accurate stakeholder update.' },
  ],
  tasks: {
    initial: {
      id: 'TAL-T1',
      round: 'initial',
      title: 'Public workshop delivery plan',
      context:
        'A fictional team must deliver a public workshop in four weeks. Venue confirmation is pending, two team members are available part time, and the budget cannot increase.',
      prompt: 'Describe dependencies, allocate responsibilities, explain a budget trade-off and draft a concise stakeholder update.',
    },
    reassessment: {
      id: 'TAL-T2',
      round: 'reassessment',
      title: 'Follow-up: the venue becomes unavailable',
      context: 'The venue becomes unavailable two weeks before launch.',
      prompt: 'Revise your plan, explain the trade-offs and identify what you would communicate first.',
    },
  },
  suggestedActivity: {
    title: 'Dependency map and risk update',
    instructions: 'Build a dependency map, identify a single point of failure and rehearse a concise risk update.',
  },
  features: { comparison: true },
  note: 'Hiring decisions are made by human reviewers and recorded separately from development findings. Physiological measurements and inferred emotions are never used to score suitability.',
  seed: {
    submissions: [
      {
        id: 'SUB-001',
        participantId: 'TAL-P01',
        taskVersionId: 'TAL-T1',
        round: 'initial',
        response:
          'Fictional work sample. Week 1: confirm venue (blocks invitations and catering). Week 2: invitations once venue is confirmed; draft materials in parallel. Week 3: logistics. Week 4: delivery. I would lead venue and stakeholder contact; the two part-time colleagues split materials and registrations. Trade-off: printed packs move to digital to keep the budget flat, which saves cost but may disadvantage attendees without devices, so we keep 20 printed copies. Update: “Workshop on track for 4 weeks; venue confirmation is the key risk — decision needed by Friday.”',
        at: seededAt,
      },
      {
        id: 'SUB-002',
        participantId: 'TAL-P02',
        taskVersionId: 'TAL-T1',
        round: 'initial',
        response:
          'Fictional work sample. Book the venue, send invitations, prepare materials and run the event. Everyone helps where needed. To stay in budget we could reduce catering. Update to stakeholders: “Planning has started and we expect a good event.”',
        at: seededAt,
      },
    ],
    reviews: [
      {
        id: 'REV-001',
        submissionId: 'SUB-001',
        participantId: 'TAL-P01',
        observations: [
          { criterionId: 'dependencies', level: 'evidenced', observation: 'Names venue confirmation as the blocking dependency and sequences invitations after it.' },
          { criterionId: 'trade-offs', level: 'evidenced', observation: 'Explains the digital-packs trade-off and who could be disadvantaged, with a mitigation.' },
          { criterionId: 'communication', level: 'partly', observation: 'Update is concise and names the risk, but does not say who decides by Friday.' },
        ],
        feedback: 'Clear sequencing around the venue dependency and a reasoned trade-off. The update would be stronger with a named decision owner.',
        openQuestions: 'Who would make the venue decision if confirmation slips past Friday?',
        outcome: 'reviewed',
        reviewerRole: 'Employer reviewer',
        at: seededReviewAt,
      },
      {
        id: 'REV-002',
        submissionId: 'SUB-002',
        participantId: 'TAL-P02',
        observations: [
          { criterionId: 'dependencies', level: 'not-yet', observation: 'Lists tasks in order but does not identify that invitations depend on venue confirmation.' },
          { criterionId: 'trade-offs', level: 'partly', observation: 'Suggests reducing catering but does not explain the consequences.' },
          { criterionId: 'communication', level: 'not-yet', observation: 'Update does not name the pending venue risk or any decision needed.' },
        ],
        feedback: 'The sample covers the main tasks. It does not yet show how dependencies or risks would be managed; a follow-up conversation could explore this.',
        openQuestions: 'How would the plan change if the venue were not confirmed in week 1? How were part-time availabilities considered?',
        outcome: 'reviewed',
        reviewerRole: 'Employer reviewer',
        at: seededReviewAt,
      },
    ],
    activities: [],
  },
}

const leadership: DevDomainConfig = {
  id: 'leadership',
  productLabel: 'Noetivra Leadership',
  title: 'Leadership Development',
  description: 'Make consequential decisions inspectable, then connect feedback to coached practice.',
  organization: 'Institutional Leadership Lab',
  participantNoun: 'Participant',
  reviewerRole: 'Coach',
  workspaces: {
    participant: {
      title: 'Participant Workspace',
      body: 'Work through a demanding scenario, explain your reasoning, review coaching feedback and revisit the decision after practice.',
      buttonLabel: 'Enter Participant Workspace',
    },
    reviewer: {
      title: 'Coach Workspace',
      body: 'Review decision reasoning against shared criteria, assign coached practice and compare how decisions change.',
      buttonLabel: 'Enter Coach Workspace',
    },
  },
  participants: [
    { id: 'LEAD-P01', name: 'Jordan Reed', description: 'Emerging team lead' },
    { id: 'LEAD-P02', name: 'Aisha Karim', description: 'Program manager' },
  ],
  criteria: [
    { id: 'trade-offs', label: 'Trade-off reasoning', description: 'Compares options and states what each risks.' },
    { id: 'communication', label: 'Communication', description: 'States uncertainty and affected groups plainly.' },
    { id: 'accountability', label: 'Accountability and follow-through', description: 'Sets a checkpoint and names who answers for it.' },
  ],
  tasks: {
    initial: {
      id: 'LEAD-T1',
      round: 'initial',
      title: 'Service outage decision',
      context:
        'A fictional organization has an outage affecting service access. A rapid workaround may restore access, but its reliability is unknown. Different teams disagree about the safest next step.',
      prompt: 'Compare options, identify uncertainty, write a stakeholder update and set an accountability checkpoint.',
    },
    reassessment: {
      id: 'LEAD-T2',
      round: 'reassessment',
      title: 'Follow-up: the workaround introduces recurring failures',
      context: 'The workaround restored some access but introduced recurring failures.',
      prompt: 'Explain how you would update the plan and communicate responsibility.',
    },
  },
  suggestedActivity: {
    title: 'Decision briefing rehearsal',
    instructions: 'Rehearse a decision briefing that states uncertainties, affected groups and a reversible checkpoint.',
  },
  features: {},
  note: 'Task performance informs coached development. It is not a universal leadership certification.',
  seed: {
    submissions: [
      {
        id: 'SUB-001',
        participantId: 'LEAD-P02',
        taskVersionId: 'LEAD-T1',
        round: 'initial',
        response:
          'Option A: apply the workaround now to restore access quickly. Option B: wait for a full fix, keeping access down longer. I would apply the workaround because access matters most and monitor it. Update: “We are restoring access using a temporary fix.” I will check in tomorrow.',
        at: seededAt,
      },
    ],
    reviews: [],
    activities: [],
  },
}

const community: DevDomainConfig = {
  id: 'community',
  productLabel: 'Noetivra for Communities',
  title: 'Civil Society & Community',
  description: 'Connect personal goals, mentorship and practical support through respectful participation.',
  organization: 'Harbor Community Network',
  participantNoun: 'Participant',
  reviewerRole: 'Program team',
  workspaces: {
    participant: {
      title: 'Participant Workspace',
      body: 'Plan a practical next step toward your goal, read mentor feedback, follow activities and see referrals made with your agreement.',
      buttonLabel: 'Enter Participant Workspace',
    },
    reviewer: {
      title: 'Program Team Workspace',
      body: 'Review plans with participants, assign activities and coordinate purpose-limited referrals with partner organizations.',
      buttonLabel: 'Enter Program Team Workspace',
    },
  },
  participants: [
    { id: 'COM-P01', name: 'Lina Brooks', description: 'Training access' },
    { id: 'COM-P02', name: 'Theo Mensah', description: 'Mentoring pathway' },
  ],
  criteria: [
    { id: 'agency', label: 'Participant agency', description: 'Keeps the participant’s choices at the centre of the plan.' },
    { id: 'planning', label: 'Practical planning', description: 'Proposes a concrete, achievable next step.' },
    { id: 'boundaries', label: 'Reciprocity and boundaries', description: 'Shares only what a partner needs, for a stated purpose.' },
  ],
  tasks: {
    initial: {
      id: 'COM-T1',
      round: 'initial',
      title: 'Training access plan',
      context:
        'A fictional participant wants to join a training program but has transport and scheduling barriers. A partner organization may help, but only the information needed for the referral should be shared.',
      prompt: 'Describe a practical next step, how you would preserve the participant’s choice and what information a partner would need.',
    },
    reassessment: {
      id: 'COM-T2',
      round: 'reassessment',
      title: 'Follow-up: the first transport option is unavailable',
      context: 'The first transport option is unavailable.',
      prompt: 'Revise the plan with the participant and explain what additional sharing, if any, would be needed.',
    },
  },
  suggestedActivity: {
    title: 'Goal, barrier and referral summary',
    instructions: 'Map one goal, one barrier and two support options; write a purpose-limited referral summary.',
  },
  features: { referrals: true },
  partners: [
    { id: 'PRT-LEARN', name: 'Harbor Learning Centre', offers: 'Training places and flexible scheduling' },
    { id: 'PRT-TRANSPORT', name: 'Community Transport Desk', offers: 'Shared rides and travel support' },
  ],
  note: 'Partners receive only the summary agreed for a stated purpose — never the participant’s complete record.',
  seed: {
    submissions: [
      {
        id: 'SUB-001',
        participantId: 'COM-P02',
        taskVersionId: 'COM-T1',
        round: 'initial',
        response:
          'First I would ask what times work and which training matters most to them. Then contact the transport desk. I would send the partner the participant’s details so they can help.',
        at: seededAt,
      },
    ],
    reviews: [],
    activities: [],
  },
}

export const developmentDomains: Record<DevDomainId, DevDomainConfig> = { education, talent, leadership, community }

export const developmentDomainIds = Object.keys(developmentDomains) as DevDomainId[]
